import clsx from "clsx";
import type { JSX } from "react";
import { useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import type { MultiValue } from "react-select";
import { searchLookup, type LookupKind } from "@/features/analytics/lookupApi";
import type { ChoiceOption } from "./types";

// Async id (uuid) -> name multi-select for the analytics Campaign category
// (campaign / segment / creative) and the provider field. Same AsyncSelect
// pattern and styling as DictMultiSelectField, but sourced from searchLookup
// (list endpoints filtered client-side) instead of the searched dictionaries.
// Values are ChoiceOption<string> so the emitted condition carries uuids.

const SEARCH_DEBOUNCE_MS = 250;

const controlClassName = (isFocused: boolean): string =>
  clsx(
    "min-h-10 rounded-(--radius-md) border bg-(--surface-1) px-3 text-[13px] text-(--text-primary) transition",
    isFocused
      ? "border-(--brand-primary)"
      : "border-(--border-subtle) hover:border-(--border-strong)",
  );

const optionClassName = (isSelected: boolean, isFocused: boolean): string =>
  clsx("cursor-pointer rounded-(--radius-md) px-3 py-2 text-[13px] transition", {
    "bg-(--pastel-violet) text-(--brand-primary) font-semibold": isSelected,
    "bg-(--surface-2) text-(--text-primary)": !isSelected && isFocused,
    "text-(--text-secondary)": !isSelected && !isFocused,
  });

const dropdownIndicatorClassName = (isFocused: boolean): string =>
  clsx("px-1 text-(--text-secondary) transition", {
    "text-(--text-primary)": isFocused,
  });

export const AsyncLookupMultiSelectField = ({
  label,
  lookup,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  lookup: LookupKind;
  value: ChoiceOption<string>[];
  onChange: (nextValue: ChoiceOption<string>[]) => void;
  placeholder?: string;
}): JSX.Element => {
  const debounceRef = useRef<number | undefined>(undefined);

  const loadOptions = useCallback(
    (inputValue: string): Promise<ChoiceOption<string>[]> => {
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current);
      }

      return new Promise((resolve, reject) => {
        debounceRef.current = window.setTimeout(() => {
          searchLookup(lookup, inputValue)
            .then((entries) => {
              resolve(
                entries.map((entry) => ({
                  value: entry.id,
                  label: entry.name,
                })),
              );
            })
            .catch(reject);
        }, SEARCH_DEBOUNCE_MS);
      });
    },
    [lookup],
  );

  return (
    <label className="block space-y-2">
      <span className="text-[12px] font-semibold text-(--text-secondary)">
        {label}
      </span>
      <AsyncSelect<ChoiceOption<string>, true>
        isMulti
        unstyled
        cacheOptions
        defaultOptions
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        loadOptions={loadOptions}
        value={value}
        placeholder={placeholder ?? "Search and select..."}
        loadingMessage={() => "Searching…"}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching options" : "Type to search"
        }
        menuPlacement="auto"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 1600,
          }),
        }}
        onChange={(nextValue: MultiValue<ChoiceOption<string>>) => {
          onChange([...nextValue]);
        }}
        classNames={{
          control: (state) => controlClassName(state.isFocused),
          valueContainer: () => "gap-1.5 py-1.5",
          placeholder: () => "text-[13px] text-(--text-muted)",
          input: () => "m-0 p-0 text-[13px] text-(--text-primary)",
          menu: () =>
            "mt-2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1)",
          menuList: () => "max-h-[248px] p-1.5",
          option: (state) => optionClassName(state.isSelected, state.isFocused),
          multiValue: () => "rounded-(--radius-sm) bg-(--pastel-violet)",
          multiValueLabel: () =>
            "px-2 py-0.5 text-[12px] font-semibold text-(--brand-primary)",
          multiValueRemove: () =>
            "flex items-center rounded-r-(--radius-sm) px-1.5 text-(--brand-primary) transition hover:bg-(--brand-primary) hover:text-(--brand-primary-contrast)",
          indicatorsContainer: () => "gap-1",
          dropdownIndicator: (state) =>
            dropdownIndicatorClassName(state.isFocused),
          clearIndicator: () =>
            "px-1 text-(--text-muted) transition hover:text-(--text-primary)",
          indicatorSeparator: () => "hidden",
          noOptionsMessage: () => "px-3 py-2 text-[13px] text-(--text-muted)",
          loadingMessage: () => "px-3 py-2 text-[13px] text-(--text-muted)",
        }}
      />
    </label>
  );
};
